import { useState, useEffect } from 'react';
import { useApp } from '../App';
import api from '../api';
import BackButton from '../components/BackButton';
import { useI18n } from '../i18n/index.jsx';

const CURRENCIES = [
  { code: 'PLN', symbol: 'zł', name: 'Злотый', flag: '🇵🇱' },
  { code: 'EUR', symbol: '€', name: 'Евро', flag: '🇪🇺' },
  { code: 'USD', symbol: '$', name: 'Доллар', flag: '🇺🇸' },
  { code: 'USDT', symbol: '₮', name: 'Tether', flag: '💎' },
];

export default function Exchange() {
  const { user } = useApp();
  const { t } = useI18n();
  const [rates, setRates] = useState(null);
  const [balances, setBalances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fromCurrency, setFromCurrency] = useState('PLN');
  const [toCurrency, setToCurrency] = useState('USDT');
  const [amount, setAmount] = useState('');
  const [preview, setPreview] = useState(null);
  const [executing, setExecuting] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (amount && parseFloat(amount) > 0) {
      calculatePreview();
    } else {
      setPreview(null);
    }
  }, [amount, fromCurrency, toCurrency]);

  async function loadData() {
    try {
      const [ratesRes, balRes] = await Promise.all([
        api.get('/exchange/rates'),
        api.get('/balance/my'),
      ]);
      setRates(ratesRes.data);
      setBalances(balRes.data.balances || []);
    } catch (e) {
      setMessage({ type: 'error', text: e.userMessage || 'Ошибка загрузки' });
    } finally {
      setLoading(false);
    }
  }

  async function calculatePreview() {
    try {
      const { data } = await api.post('/exchange/calculate', {
        from_currency: fromCurrency,
        to_currency: toCurrency,
        amount: parseFloat(amount),
      });
      setPreview(data);
    } catch (e) {
      setPreview(null);
    }
  }

  async function executeExchange() {
    if (!preview) return;
    setExecuting(true);
    try {
      await api.post('/exchange/execute', {
        from_currency: fromCurrency,
        to_currency: toCurrency,
        amount: parseFloat(amount),
      });
      setMessage({ type: 'success', text: `✅ Обмен выполнен: ${amount} ${fromCurrency} → ${preview.to_amount} ${toCurrency}` });
      setAmount('');
      setPreview(null);
      loadData();
    } catch (e) {
      setMessage({ type: 'error', text: e.userMessage || 'Ошибка обмена' });
    } finally {
      setExecuting(false);
    }
  }

  function getBalance(currency) {
    const b = balances.find(b => b.currency === currency);
    return b ? b.amount : 0;
  }

  function swapCurrencies() {
    const temp = fromCurrency;
    setFromCurrency(toCurrency);
    setToCurrency(temp);
  }

  if (loading) return <div className="page"><BackButton /><div className="loader">{t('common.loading')}</div></div>;

  const isSuperadmin = user?.role === 'superadmin';

  return (
    <div className="page">
      <BackButton />
      <h1>{t('exchange.title')}</h1>

      {message && (
        <div style={{
          padding: 12,
          borderRadius: 8,
          marginBottom: 16,
          background: message.type === 'error' ? 'rgba(220,38,38,0.2)' : 'rgba(16,185,129,0.2)',
        }}>
          {message.text}
        </div>
      )}

      {/* Current Rates */}
      <div style={{
        background: 'rgba(255,255,255,0.05)',
        borderRadius: 12,
        padding: 16,
        marginBottom: 16,
      }}>
        <div style={{ fontWeight: 'bold', marginBottom: 8 }}>📊 Текущие курсы</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, fontSize: 13 }}>
          <div>USDT/PLN: <b>{rates?.usdt_pln}</b></div>
          <div>EUR/PLN: <b>{rates?.eur_pln}</b></div>
          <div>USD/PLN: <b>{rates?.usd_pln}</b></div>
        </div>
        <div style={{ fontSize: 12, opacity: 0.7, marginTop: 8 }}>
          Комиссия покупки USDT: {rates?.commission_buy_usdt}% | Продажа: {rates?.commission_sell_usdt}%
        </div>
      </div>

      {/* Balances */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(4, 1fr)',
        gap: 8,
        marginBottom: 16,
      }}>
        {CURRENCIES.map(c => (
          <div key={c.code} style={{
            background: 'rgba(255,255,255,0.05)',
            borderRadius: 8,
            padding: 12,
            textAlign: 'center',
          }}>
            <div style={{ fontSize: 20 }}>{c.flag}</div>
            <div style={{ fontWeight: 'bold' }}>{getBalance(c.code).toFixed(2)}</div>
            <div style={{ fontSize: 11, opacity: 0.7 }}>{c.code}</div>
          </div>
        ))}
      </div>

      {/* Exchange Form */}
      <div style={{
        background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
        borderRadius: 12,
        padding: 20,
      }}>
        <div style={{ marginBottom: 16 }}>
          <label style={{ display: 'block', marginBottom: 4, opacity: 0.8 }}>Отдаю:</label>
          <div style={{ display: 'flex', gap: 8 }}>
            <select
              value={fromCurrency}
              onChange={(e) => setFromCurrency(e.target.value)}
              style={{
                padding: 12,
                borderRadius: 8,
                border: 'none',
                background: 'rgba(255,255,255,0.2)',
                color: '#fff',
                fontSize: 16,
              }}
            >
              {CURRENCIES.map(c => (
                <option key={c.code} value={c.code}>{c.flag} {c.code}</option>
              ))}
            </select>
            <input
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0.00"
              style={{
                flex: 1,
                padding: 12,
                borderRadius: 8,
                border: 'none',
                background: 'rgba(255,255,255,0.2)',
                color: '#fff',
                fontSize: 16,
              }}
            />
          </div>
          <div style={{ fontSize: 12, opacity: 0.7, marginTop: 4 }}>
            Баланс: {getBalance(fromCurrency).toFixed(2)} {fromCurrency}
          </div>
        </div>

        <div style={{ textAlign: 'center', marginBottom: 16 }}>
          <button
            onClick={swapCurrencies}
            style={{
              background: 'rgba(255,255,255,0.2)',
              border: 'none',
              borderRadius: '50%',
              width: 40,
              height: 40,
              cursor: 'pointer',
              fontSize: 20,
            }}
          >
            ⇅
          </button>
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={{ display: 'block', marginBottom: 4, opacity: 0.8 }}>Получаю:</label>
          <div style={{ display: 'flex', gap: 8 }}>
            <select
              value={toCurrency}
              onChange={(e) => setToCurrency(e.target.value)}
              style={{
                padding: 12,
                borderRadius: 8,
                border: 'none',
                background: 'rgba(255,255,255,0.2)',
                color: '#fff',
                fontSize: 16,
              }}
            >
              {CURRENCIES.map(c => (
                <option key={c.code} value={c.code}>{c.flag} {c.code}</option>
              ))}
            </select>
            <div style={{
              flex: 1,
              padding: 12,
              borderRadius: 8,
              background: 'rgba(255,255,255,0.1)',
              fontSize: 16,
              fontWeight: 'bold',
            }}>
              {preview ? preview.to_amount.toFixed(2) : '0.00'}
            </div>
          </div>
        </div>

        {preview && (
          <div style={{
            background: 'rgba(0,0,0,0.2)',
            borderRadius: 8,
            padding: 12,
            marginBottom: 16,
            fontSize: 13,
          }}>
            <div>Курс: 1 {fromCurrency} = {preview.rate} {toCurrency}</div>
            {preview.commission_percent > 0 && (
              <div>Комиссия: {preview.commission_percent}% ({preview.commission_amount} {toCurrency})</div>
            )}
          </div>
        )}

        <button
          className="btn btn-primary"
          style={{ width: '100%', background: 'rgba(255,255,255,0.2)' }}
          onClick={executeExchange}
          disabled={executing || !preview || parseFloat(amount) > getBalance(fromCurrency)}
        >
          {executing ? '⏳ Обмен...' : '💱 Обменять'}
        </button>
      </div>
    </div>
  );
}
